import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { ArrowRight, Award, Globe, Leaf, Package, Palette, Recycle, Sparkles, Target, Truck, Users, Zap } from "lucide-react";
import { AnimatedStat } from "../components/AnimatedStat";
import { BrandLogosMarquee } from "../components/BrandLogosMarquee";
import {
  GradientSectionHeading,
  GradientSectionHeadingOnDark,
  SectionHeadingOnDarkStatic,
} from "../components/GradientSectionHeading";
import { HeroInteractiveDotGrid } from "../components/HeroInteractiveDotGrid";
import { PackagingHeadlineDecor } from "../components/PackagingHeadlineDecor";
import { useInViewOnce } from "../hooks/useInViewOnce";
import { cn } from "../components/ui/utils";

/**
 * “Packaging” only — fully opaque stops. `HEADLINE_GRADIENT` ends in transparent; on a short word,
 * `background-position: 0%` + `background-size: 300%` can clip into that band and hide the text.
 */
const PACKAGING_GRADIENT =
  "linear-gradient(90deg,#53A4CF 00%,#DA5B9B 15%,#F3D573 25%,#000000 33%)";

/** 0.1s CSS delay + 1.2s sweep + buffer — “Packaging” gradient only */
const HERO_PACKAGING_SWEEP_SETTLE_MS = 1350;

const MATERIALS_DIAMOND_PATH =
  "M15.999 1.99059e-07C16.1168 -8.5611e-05 16.2329 0.0275728 16.3381 0.0807354C16.4432 0.133898 16.5343 0.21107 16.604 0.306L16.659 0.393L19.922 6.421L19.96 6.514L19.972 6.554L19.992 6.656L19.998 6.75C19.9975 6.81754 19.9884 6.88474 19.971 6.95L19.924 7.075L19.886 7.144C19.8632 7.17955 19.8381 7.21362 19.811 7.246L19.871 7.168L19.846 7.203L10.596 17.708C10.5201 17.811 10.4173 17.8913 10.299 17.94L10.201 17.973L10.123 17.99L10 18L9.9 17.994L9.782 17.968C9.72082 17.9507 9.66228 17.9251 9.608 17.892L9.599 17.885C9.55168 17.8556 9.50804 17.8207 9.469 17.781L0.179 7.235L0.153 7.203L0.113 7.144C0.056483 7.05243 0.0203315 6.94978 0.00699997 6.843L0 6.75L0.00300002 6.684L0.016 6.595L0.04 6.509L0.062 6.45L0.0899999 6.393L3.34 0.393C3.39603 0.289458 3.47565 0.200561 3.57242 0.133503C3.66918 0.0664441 3.78038 0.0231077 3.897 0.00700017L3.999 1.99059e-07H15.999ZM12.997 7.5H6.998L9.998 15.187L12.997 7.5ZM5.389 7.5H2.409L7.76 13.574L5.389 7.5ZM17.587 7.5H14.608L12.24 13.57L17.587 7.5ZM6.971 1.5H4.445L2.007 6H5.531L6.971 1.5ZM11.451 1.5H8.546L7.105 6H12.89L11.451 1.5ZM15.551 1.5H13.026L14.466 6H17.988L15.551 1.5Z";

function MaterialsDiamondIcon() {
  return (
    <svg
      width={20}
      height={18}
      viewBox="0 0 20 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
      aria-hidden
    >
      <path d={MATERIALS_DIAMOND_PATH} fill="currentColor" />
    </svg>
  );
}

function SustainableOptionsCardIcon() {
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
      aria-hidden
    >
      <path
        d="M7.00001 19H4.81501C4.49958 19.0009 4.18927 18.9203 3.91418 18.7659C3.6391 18.6116 3.40859 18.3887 3.24501 18.119C3.08792 17.8481 3.00486 17.5407 3.00415 17.2275C3.00345 16.9144 3.08514 16.6066 3.24101 16.335L7.19601 9.5"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11 18.9998H19.203C19.5169 18.9974 19.8249 18.9143 20.0974 18.7584C20.3699 18.6025 20.5978 18.3792 20.759 18.1098C20.9139 17.8397 20.9954 17.5337 20.9954 17.2223C20.9954 16.9109 20.9139 16.605 20.759 16.3348L19.533 14.2148"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 16L11 19L14 22"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.2931 13.596L7.1961 9.5L3.1001 10.598"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.34399 5.811L10.437 3.919C10.5939 3.6454 10.8189 3.41701 11.0902 3.25599C11.3614 3.09497 11.6696 3.00679 11.985 3C12.2979 2.99942 12.6055 3.08116 12.8769 3.23703C13.1482 3.39289 13.3738 3.6174 13.531 3.888L17.474 10.731"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.3779 9.63277L17.4739 10.7308L18.5709 6.63477"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Matches files in `public/` (spaces → %20 in URL) */
const PRODUCT_RANGE_IMG = {
  textile: "/products/textile.jpeg",
  garment: "/products/garment.jpeg",
  luxury: "/products/luxury.jpeg",
  label: "/products/label.jpeg",
  custom: "/products/custom.jpeg",
  retail: "/products/retail.jpeg",
  eco: "/products/eco.jpeg",
  business: "/products/business.jpeg",
} as const;

function ProductRangeCoverImage({ src }: { src: string }) {
  return (
    <div className="relative aspect-[4/3] overflow-hidden">
      <img
        src={src}
        alt="Custom packaging product by Colour Club Printers Faisalabad Pakistan"
        className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
        width="600"
        height="450"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

export function HomePage() {
  const heroSectionRef = useRef<HTMLElement | null>(null);
  const heroHeadline = useInViewOnce({ threshold: 0.3, rootMargin: "0px 0px -10% 0px" });
  const [packagingGradientSettled, setPackagingGradientSettled] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    if (!heroHeadline.inView) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setPackagingGradientSettled(true);
      return;
    }
    const t = window.setTimeout(() => setPackagingGradientSettled(true), HERO_PACKAGING_SWEEP_SETTLE_MS);
    return () => window.clearTimeout(t);
  }, [heroHeadline.inView]);

  const packagingAnimating = heroHeadline.inView && !packagingGradientSettled;

  return (
    <div className="min-h-screen min-w-0 overflow-x-hidden bg-[#F6F2EB]">
      <section
        ref={heroSectionRef}
        className="relative flex min-h-[min(840px,94vh)] w-full flex-col items-center justify-center overflow-hidden bg-[#F6F2EB]"
      >
        <HeroInteractiveDotGrid rootRef={heroSectionRef} />
        <div className="relative z-10 mx-auto w-full max-w-6xl px-4 py-8 text-center sm:py-12 md:px-6 md:py-14 lg:py-16">
          <div className="text-hero-badge-fluid mx-auto mb-6 inline-flex min-h-8 w-max max-w-[calc(100vw-2rem)] flex-nowrap items-center justify-center gap-1 rounded-full border border-border bg-background px-2 py-1.5 font-medium leading-tight text-foreground sm:mb-8 sm:min-h-9 sm:gap-1.5 sm:px-2.5 sm:leading-normal md:px-4">
            <Sparkles className="size-3 shrink-0 text-foreground sm:size-3.5 md:size-4" aria-hidden />
            <span className="sm:whitespace-nowrap">Premium Packaging Solutions Since 1996</span>
          </div>

          <h1
            ref={heroHeadline.ref}
            className="text-hero-headline-fluid mx-auto mb-4 flex max-w-5xl flex-col items-center gap-[0.08em] text-center font-bold leading-[1.02] tracking-tight sm:mb-6 sm:gap-[0.1em] sm:leading-[1.01]"
          >
            <span className="block w-full text-foreground text-center">Your Best Solution for</span>
            <span className="flex w-full flex-col sm:flex-row items-center sm:items-baseline justify-center gap-0 sm:gap-x-[0.25em]">
              <span className="inline-block shrink-0 leading-[1.05] pb-[0.2em] text-foreground text-center sm:text-left">Custom</span>
              <span className="inline-flex items-center justify-center gap-x-1 sm:gap-x-0 -mt-1.5 sm:mt-0 sm:relative">
                <span
                  className={cn(
                    "inline-block",
                    !heroHeadline.inView && "pb-[0.2em] leading-[1.05] text-foreground",
                    packagingAnimating &&
                    "gradient-text-animate gradient-text-animate--tight gradient-text-animate--tight-descenders gradient-text-animate--packaging-word gradient-text-animate--play",
                    heroHeadline.inView &&
                    !packagingAnimating &&
                    "gradient-text-animate gradient-text-animate--tight gradient-text-animate--tight-descenders gradient-text-animate--packaging-word gradient-text-animate--packaging-at-rest",
                  )}
                  style={heroHeadline.inView ? { backgroundImage: PACKAGING_GRADIENT } : undefined}
                >
                  Packaging
                </span>
                {/* In-flow on mobile for group centering; Absolute on desktop to prevent shifting */}
                <span
                  className="pointer-events-none -translate-y-[0.3em] sm:translate-y-0 sm:absolute sm:left-full ml-0 flex h-[0.8em] w-auto shrink-0 sm:top-[0em] items-center"
                  aria-hidden
                >
                  <PackagingHeadlineDecor className="h-full w-auto object-contain object-left" />
                </span>
              </span>
            </span>
          </h1>

          <p className="text-hero-sub-fluid mx-auto mb-8 max-w-3xl px-1 leading-relaxed text-muted-foreground sm:mb-10 md:mb-12">
            End-to-end packaging solutions for global brands. From design to delivery, we bring your vision to life with premium materials, expert craftsmanship, and sustainable practices.
          </p>

          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[#131312] text-sm font-medium text-white transition-colors hover:bg-[#1c1c1b] shadow-[0_4px_6px_-4px_rgba(0,0,0,0.22),0_10px_15px_-3px_rgba(0,0,0,0.18)] sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-4 sm:size-5" />
            </Link>
            <Link
              to="/products"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-[rgba(0,0,0,0.16)] bg-[#F6F2EB] px-5 text-sm font-medium text-foreground transition-colors hover:bg-[#efe9e0] sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Explore Products
            </Link>
          </div>

        </div>
      </section>

      {/* <section className="bg-background pt-12 pb-6 sm:pt-16 sm:pb-8 md:pt-20 md:pb-10">
        <div className="mx-auto grid w-full max-w-5xl grid-cols-2 place-items-center gap-y-8 gap-x-4 px-6 sm:gap-y-10 md:grid-cols-4 md:gap-10 lg:px-8">
          {([
            { end: 30, suffix: "+", label: "Years Experience", durationMs: undefined },
            { end: 500, suffix: "+", label: "Global Clients", durationMs: 2400 },
            { end: 50, suffix: "M+", label: "Units Produced", durationMs: undefined },
            { end: 98, suffix: "%", label: "Client Satisfaction", durationMs: undefined },
          ] as const).map((s) => (
            <div key={s.label} className="flex flex-col items-center justify-center text-center">
              <AnimatedStat
                end={s.end}
                suffix={s.suffix}
                durationMs={s.durationMs}
                className="mb-1 w-full text-center font-bold text-foreground sm:mb-1.5"
                style={{ fontSize: "clamp(1.25rem, 0.65rem + 2vw, 2.25rem)", lineHeight: 1.1 }}
              />
              <div
                className="text-muted-foreground"
                style={{ fontSize: "clamp(0.75rem, 0.55rem + 0.6vw, 1rem)", lineHeight: 1.3 }}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section> */}

      <section className="py-24 bg-background">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <GradientSectionHeading className="mb-6 text-center text-4xl font-semibold leading-[1.1] tracking-tight md:text-6xl">
              Trusted by Leading Brands
            </GradientSectionHeading>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Experience working with international retail and textile companies across UK, EU, and USA
            </p>
          </div>

          <div className="mb-16">
            <div className="rounded-3xl border border-border bg-[#F6F2EB] p-8 md:p-12">
              <BrandLogosMarquee />
              <div className="text-center mt-10 md:mt-12">
                <p className="text-lg text-foreground/70 italic">
                  "Partnering with leading UK retail brands and international textile companies to deliver exceptional packaging solutions"
                </p>
              </div>
            </div>
          </div>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="icon-box flex-shrink-0 size-16 mb-6">
                <Globe className="size-8" />
              </div>
              <h3 className="text-xl font-semibold mb-3">International Reach</h3>
              <p className="text-muted-foreground">
                Serving clients across UK, Europe, and North America with reliable logistics and timely delivery
              </p>
            </div>

            <div className="text-center p-8">
              <div className="icon-box flex-shrink-0 size-16 mb-6">
                <Award className="size-8" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Certified Excellence</h3>
              <p className="text-muted-foreground">
                FSC certified, ISO 9001 compliant, and Sedex registered for ethical and quality standards
              </p>
            </div>

            <div className="text-center p-8">
              <div className="icon-box flex-shrink-0 size-16 mb-6">
                <Users className="size-8" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Dedicated Support</h3>
              <p className="text-muted-foreground">
                Expert consultation from design to delivery, ensuring your packaging exceeds expectations
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F6F2EB] py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <GradientSectionHeading className="mb-6 text-center text-4xl font-semibold leading-[1.1] tracking-tight md:text-6xl">Our Product Range</GradientSectionHeading>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive packaging solutions tailored to your industry needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Link to="/products/textile-home-packaging" className="group relative overflow-hidden rounded-2xl border border-border bg-white transition-all duration-300 hover:shadow-2xl">
              <ProductRangeCoverImage src={PRODUCT_RANGE_IMG.textile} />
              <div className="p-8">
                <h3 className="text-2xl font-semibold mb-3 text-[#131312] transition-colors">Textile & Home Packaging</h3>
                <p className="text-muted-foreground mb-4">
                  Custom textile and home packaging solutions designed for bedding, home fabrics, and lifestyle products.
                </p>
                <span className="inline-flex items-center font-medium text-[#131312]">
                  Explore <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>

            <Link to="/products/garment-packaging" className="group relative overflow-hidden rounded-2xl border border-border bg-white transition-all duration-300 hover:shadow-2xl">
              <ProductRangeCoverImage src={PRODUCT_RANGE_IMG.garment} />
              <div className="p-8">
                <h3 className="text-2xl font-semibold mb-3 text-[#131312] transition-colors">Garment Packaging</h3>
                <p className="text-muted-foreground mb-4">
                  Complete garment packaging solutions including labels, tags, and branding elements for fashion and apparel brands.
                </p>
                <span className="inline-flex items-center font-medium text-[#131312]">
                  Explore <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>

            <Link to="/products/luxury-packaging" className="group relative overflow-hidden rounded-2xl border border-border bg-white transition-all duration-300 hover:shadow-2xl">
              <ProductRangeCoverImage src={PRODUCT_RANGE_IMG.luxury} />
              <div className="p-8">
                <h3 className="text-2xl font-semibold mb-3 text-[#131312] transition-colors">Luxury Packaging</h3>
                <p className="text-muted-foreground mb-4">
                  High-end luxury packaging solutions crafted with premium materials and finishes for exclusive products.
                </p>
                <span className="inline-flex items-center font-medium text-[#131312]">
                  Explore <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>

            <Link to="/products/labels-stickers" className="group relative overflow-hidden rounded-2xl border border-border bg-white transition-all duration-300 hover:shadow-2xl">
              <ProductRangeCoverImage src={PRODUCT_RANGE_IMG.label} />
              <div className="p-8">
                <h3 className="text-2xl font-semibold mb-3 text-[#131312] transition-colors">Labels & Stickers</h3>
                <p className="text-muted-foreground mb-4">
                  High-quality labels and stickers for product identification, retail, and logistics applications.
                </p>
                <span className="inline-flex items-center font-medium text-[#131312]">
                  Explore <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>

            <Link to="/products/custom-packaging" className="group relative overflow-hidden rounded-2xl border border-border bg-white transition-all duration-300 hover:shadow-2xl">
              <ProductRangeCoverImage src={PRODUCT_RANGE_IMG.custom} />
              <div className="p-8">
                <h3 className="text-2xl font-semibold mb-3 text-[#131312] transition-colors">Custom Packaging</h3>
                <p className="text-muted-foreground mb-4">
                  Fully customized packaging solutions tailored to your product, brand, and industry requirements.
                </p>
                <span className="inline-flex items-center font-medium text-[#131312]">
                  Explore <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>

            <Link to="/products/retail-display-packaging" className="group relative overflow-hidden rounded-2xl border border-border bg-white transition-all duration-300 hover:shadow-2xl">
              <ProductRangeCoverImage src={PRODUCT_RANGE_IMG.retail} />
              <div className="p-8">
                <h3 className="text-2xl font-semibold mb-3 text-[#131312] transition-colors">Retail & Display Packaging</h3>
                <p className="text-muted-foreground mb-4">
                  Retail and display packaging solutions designed to improve product visibility and increase in-store conversions.
                </p>
                <span className="inline-flex items-center font-medium text-[#131312]">
                  Explore <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>

          </div>

          <div className="mt-12 text-center">
            <Link to="/products" className="inline-flex items-center font-medium text-[#131312] text-lg">
              View All Products <ArrowRight className="ml-2 size-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <GradientSectionHeading className="mb-6 text-center text-4xl font-semibold leading-[1.1] tracking-tight md:text-6xl">
              Why Choose Colour Club Printers
            </GradientSectionHeading>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Three decades of excellence in packaging and printing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="p-6">
              <div className="icon-box flex-shrink-0 size-14 mb-6">
                <Target className="size-7" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Precision Quality</h3>
              <p className="text-muted-foreground">
                State-of-the-art printing technology ensuring consistent, high-quality results
              </p>
            </div>

            <div className="p-6">
              <div className="icon-box flex-shrink-0 size-14 mb-6">
                <Zap className="size-7" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Fast Turnaround</h3>
              <p className="text-muted-foreground">
                Efficient production processes with quick sampling and delivery timelines
              </p>
            </div>

            <div className="p-6">
              <div className="icon-box flex-shrink-0 size-14 mb-6">
                <Palette className="size-7" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Custom Design</h3>
              <p className="text-muted-foreground">
                In-house design team to bring your vision to life with expert guidance
              </p>
            </div>

            <div className="p-6">
              <div className="icon-box flex-shrink-0 size-14 mb-6">
                <Recycle className="size-7" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Eco Conscious</h3>
              <p className="text-muted-foreground">
                FSC certified materials and sustainable production practices
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#131312] py-24 text-[#FFFFFF]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <SectionHeadingOnDarkStatic className="mb-6 text-center text-4xl font-semibold leading-[1.1] tracking-tight md:text-6xl">
              360° Packaging Solutions
            </SectionHeadingOnDarkStatic>
            <p className="text-xl text-[#FFFFFF] max-w-3xl mx-auto">
              Complete end-to-end capabilities under one roof
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="rounded-2xl border border-border bg-white/10 p-8 backdrop-blur-sm">
              <h3 className="text-2xl font-semibold mb-4 text-[#FFFFFF]">Design & Prototyping</h3>
              <ul className="space-y-3 text-[#FFFFFF]">
                <li className="flex items-start">
                  <ArrowRight className="size-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Expert packaging design consultation</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="size-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>3D mockups and virtual prototypes</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="size-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Physical sampling before production</span>
                </li>
              </ul>
            </div>

            <div className="rounded-2xl border border-border bg-white/10 p-8 backdrop-blur-sm">
              <h3 className="text-2xl font-semibold mb-4 text-[#FFFFFF]">Production & Finishing</h3>
              <ul className="space-y-3 text-[#FFFFFF]">
                <li className="flex items-start">
                  <ArrowRight className="size-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Offset, digital, and UV printing</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="size-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Premium finishes: foil, emboss, UV</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="size-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Quality control at every stage</span>
                </li>
              </ul>
            </div>

            <div className="rounded-2xl border border-border bg-white/10 p-8 backdrop-blur-sm">
              <h3 className="text-2xl font-semibold mb-4 text-[#FFFFFF]">Logistics & Fulfillment</h3>
              <ul className="space-y-3 text-[#FFFFFF]">
                <li className="flex items-start">
                  <ArrowRight className="size-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Global shipping and distribution</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="size-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Inventory management solutions</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="size-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Just-in-time delivery options</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F6F2EB] py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <GradientSectionHeading className="mb-6 text-center text-4xl font-semibold leading-[1.1] tracking-tight md:text-6xl">
              Premium Materials & Finishes
            </GradientSectionHeading>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Explore our comprehensive library of materials and finishing options
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <Link to="/options?category=paperboard" className="group rounded-2xl border border-border bg-white p-8 transition-all hover:shadow-2xl">
              <div className="mb-6 flex size-16 items-center justify-center rounded-xl border border-border bg-[#F6F2EB] text-[#131312]">
                <svg
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="shrink-0"
                  aria-hidden
                >
                  <path
                    d="M10 9.5C10 12.538 8.21 15 6 15C3.79 15 2 12.538 2 9.5C2 6.462 3.79 4 6 4C8.21 4 10 6.462 10 9.5Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M21.813 10.694C21.933 10.165 22 9.594 22 9C22 6.239 20.576 4 18.818 4H6C9.155 4 10.57 7.955 10.023 10.567C9.456 13.267 7.179 16.369 7.389 19.131C7.426 19.622 7.799 20 8.247 20H18.36C18.852 20 19.238 19.538 19.197 19C18.993 16.308 21.212 13.313 21.813 10.694Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M6.125 9.5H6M6.25 9.5C6.25 9.5663 6.22366 9.62989 6.17678 9.67678C6.12989 9.72366 6.0663 9.75 6 9.75C5.9337 9.75 5.87011 9.72366 5.82322 9.67678C5.77634 9.62989 5.75 9.5663 5.75 9.5C5.75 9.4337 5.77634 9.37011 5.82322 9.32322C5.87011 9.27634 5.9337 9.25 6 9.25C6.0663 9.25 6.12989 9.27634 6.17678 9.32322C6.22366 9.37011 6.25 9.4337 6.25 9.5Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-[#131312] transition-colors">Paperboard Materials</h3>
              <p className="text-muted-foreground mb-4">
                SBS C1S, SBS C2S, Kraft, and Duplex board options for various applications
              </p>
              <span className="font-medium text-[#131312]">Explore Materials →</span>
            </Link>

            <Link to="/options?category=finishes" className="group rounded-2xl border border-border bg-white p-8 transition-all hover:shadow-2xl">
              <div className="mb-6 flex size-16 items-center justify-center rounded-xl border border-border bg-[#F6F2EB] text-[#131312]">
                <MaterialsDiamondIcon />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-[#131312] transition-colors">Premium Finishes</h3>
              <p className="text-muted-foreground mb-4">
                Foil stamping, emboss/deboss, spot UV, and soft-touch coatings
              </p>
              <span className="font-medium text-[#131312]">Explore Finishes →</span>
            </Link>

            <Link to="/sustainability" className="group rounded-2xl border border-border bg-white p-8 transition-all hover:shadow-2xl">
              <div className="mb-6 flex size-16 items-center justify-center rounded-xl border border-border bg-[#F6F2EB] text-[#131312]">
                <SustainableOptionsCardIcon />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-[#131312] transition-colors">Sustainable Options</h3>
              <p className="text-muted-foreground mb-4">
                FSC certified, recycled, and biodegradable materials for eco-conscious brands
              </p>
              <span className="font-medium text-[#131312]">Explore Sustainability →</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="mx-auto mb-6 inline-flex max-w-full items-center justify-center gap-1.5 rounded-full border border-[rgba(0,0,0,0.08)] bg-[#F6F2EB] px-2.5 py-2 text-[0.8125rem] font-medium leading-none text-foreground sm:gap-2 sm:px-4 sm:text-sm">
              <Leaf className="size-3.5 shrink-0 sm:size-4" aria-hidden />
              <span className="whitespace-nowrap">Committed to Sustainability</span>
            </div>
            <GradientSectionHeading className="mb-6 text-center text-4xl font-semibold leading-[1.1] tracking-tight md:text-6xl">Building a Greener Future</GradientSectionHeading>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Environmental responsibility is at the core of everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="relative aspect-square overflow-hidden rounded-3xl border border-border bg-[#F6F2EB]">
                <img
                  src="/Greener.png"
                  alt="Eco-friendly sustainable packaging by Colour Club Printers — FSC certified packaging manufacturer Pakistan"
                  className="h-full w-full object-cover object-center"
                  width="600"
                  height="600"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="icon-box flex-shrink-0 size-12">
                  <Award className="size-6" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">FSC Certified</h3>
                  <p className="text-muted-foreground">
                    All our materials are sourced from responsibly managed forests with FSC certification
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="icon-box flex-shrink-0 size-12">
                  <Recycle className="size-6" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Recycled Materials</h3>
                  <p className="text-muted-foreground">
                    Wide range of recycled and recyclable materials to minimize environmental impact
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="icon-box flex-shrink-0 size-12">
                  <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24.75 1.55V23.95C24.75 24.1622 24.6657 24.3657 24.5157 24.5157C24.3657 24.6657 24.1622 24.75 23.95 24.75H1.55C1.33783 24.75 1.13434 24.6657 0.984315 24.5157C0.834286 24.3657 0.75 24.1622 0.75 23.95V1.55C0.75 1.33783 0.834286 1.13434 0.984315 0.984315C1.13434 0.834286 1.33783 0.75 1.55 0.75H23.95C24.1622 0.75 24.3657 0.834286 24.5157 0.984315C24.6657 1.13434 24.75 1.33783 24.75 1.55Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15.4168 10.0832V9.8165C15.4168 9.50133 15.3548 9.18925 15.2341 8.89806C15.1135 8.60688 14.9367 8.34231 14.7139 8.11945C14.491 7.89659 14.2265 7.7198 13.9353 7.59919C13.6441 7.47858 13.332 7.4165 13.0168 7.4165H12.4835C11.847 7.4165 11.2365 7.66936 10.7864 8.11945C10.3364 8.56953 10.0835 9.17998 10.0835 9.8165V15.6832C10.0835 16.3197 10.3364 16.9301 10.7864 17.3802C11.2365 17.8303 11.847 18.0832 12.4835 18.0832H13.0168C13.6533 18.0832 14.2638 17.8303 14.7139 17.3802C15.164 16.9301 15.4168 16.3197 15.4168 15.6832V15.4165" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Carbon Neutral Goal</h3>
                  <p className="text-muted-foreground">
                    Committed to reducing our carbon footprint with sustainable production practices
                  </p>
                </div>
              </div>

              <Link to="/sustainability" className="inline-flex items-center font-medium text-[#131312]">
                Learn more about our sustainability efforts
                <ArrowRight className="ml-2 size-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F6F2EB] py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <GradientSectionHeading className="mb-6 text-center text-4xl font-semibold leading-[1.1] tracking-tight md:text-6xl">Our Process</GradientSectionHeading>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Simple, transparent, and efficient from inquiry to delivery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { step: "01", title: "Inquiry", icon: "lucide-users", desc: "Contact us with your requirements" },
              { step: "02", title: "Quote", icon: "custom-quote", desc: "Receive detailed pricing" },
              { step: "03", title: "Sampling", icon: "custom-sampling", desc: "Approve physical samples" },
              { step: "04", title: "Production", icon: "custom-production", desc: "Manufacturing begins" },
              { step: "05", title: "Delivery", icon: "lucide-truck", desc: "Global shipping arranged" },
            ].map((item) => (
              <div key={item.step} className="relative rounded-2xl border border-border bg-white p-6 text-center">
                <div className="mb-4 text-6xl font-bold text-[#1A1A1933]">{item.step}</div>
                <div className="icon-box flex-shrink-0 size-14 mb-4">
                  {item.icon === "lucide-users" && <Users className="size-7" />}
                  {item.icon === "lucide-truck" && <Truck className="size-7" />}
                  {item.icon === "custom-quote" && (
                    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.2734 17.9645C2.41942 16.1093 1.49183 15.1829 1.14744 13.9793C0.801842 12.7757 1.09704 11.4978 1.68743 8.943L2.02703 7.46942C2.52262 5.31904 2.77102 4.24385 3.50661 3.50706C4.2422 2.77027 5.31859 2.52308 7.46896 2.02748L8.94254 1.68669C11.4985 1.09749 12.7753 0.802297 13.9789 1.14669C15.1825 1.49229 16.1089 2.41988 17.9628 4.27385L20.1588 6.46983C23.388 9.69779 24.9995 11.3106 24.9995 13.3145C24.9995 15.3197 23.3868 16.9325 20.16 20.1593C16.932 23.3872 15.3193 25 13.3141 25C11.3101 25 9.69613 23.3872 6.46937 20.1605L4.2734 17.9645Z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M17.068 17.0681C17.77 16.3637 17.8648 15.3197 17.2792 14.7329C16.6936 14.1461 15.6484 14.2421 14.9452 14.9453C14.2432 15.6485 13.1981 15.7433 12.6125 15.1577C12.0269 14.5721 12.1217 13.5269 12.8249 12.825M12.8249 12.825L12.4001 12.4002M12.8249 12.825C13.2221 12.4266 13.7284 12.225 14.2 12.2286M17.4916 17.4917L17.0668 17.0669C16.5868 17.5481 15.9448 17.7449 15.4 17.6321" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M10.6253 10.9524C11.5625 10.0152 11.5625 8.49558 10.6253 7.55834C9.68804 6.62109 8.16846 6.62109 7.23122 7.55834C6.29397 8.49558 6.29397 10.0152 7.23121 10.9524C8.16846 11.8897 9.68804 11.8897 10.6253 10.9524Z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                  {item.icon === "custom-sampling" && (
                    <svg width="26" height="24" viewBox="0 0 26 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11.6611 0C11.3375 0 11.0139 0.0757896 10.7027 0.189474L1.54142 4.0421C0.93245 4.29517 0.447436 4.7833 0.193013 5.39916C-0.0614104 6.01502 -0.0644157 6.70821 0.184658 7.32632L6.35856 22.4211C6.54093 22.8789 6.85152 23.2724 7.25193 23.5531C7.65235 23.8337 8.12503 23.9891 8.61154 24C8.93517 24 9.27125 23.9621 9.59488 23.8105L18.7686 19.9579C19.2114 19.7672 19.591 19.4517 19.8623 19.0486C20.1337 18.6456 20.2855 18.1721 20.2996 17.6842C20.3121 17.3684 20.2498 17.0021 20.1378 16.6737L13.9141 1.57895C13.7388 1.11626 13.4299 0.718029 13.028 0.436325C12.626 0.154621 12.1496 0.0025517 11.6611 0ZM15.9928 0L20.2872 10.5474V2.52632C20.2872 1.8563 20.0249 1.21372 19.558 0.739941C19.0912 0.266165 18.458 0 17.7977 0M22.7891 1.94526V13.3516L25.8138 5.94947C25.9378 5.64349 26.0011 5.31568 26 4.98484C25.9989 4.65399 25.9334 4.32662 25.8074 4.0215C25.6813 3.71639 25.4972 3.43952 25.2654 3.20679C25.0337 2.97405 24.759 2.79003 24.4571 2.66526M11.6611 2.48842L17.8599 17.6716L8.66133 21.5368L2.46253 6.36632" stroke="currentColor" strokeWidth="1.2" fill="none"/>
                    </svg>
                  )}
                  {item.icon === "custom-production" && (
                    <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11.7779 0.248662C11.8912 0.183943 12.0194 0.149902 12.1499 0.149902C12.2804 0.149902 12.4086 0.183943 12.5219 0.248662L18.1469 3.46323C18.2617 3.5288 18.3571 3.62355 18.4235 3.73787C18.4899 3.8522 18.5249 3.98204 18.5249 4.11424V10.1069L23.7719 13.1069C23.8863 13.1722 23.9814 13.2665 24.0477 13.3803C24.1141 13.494 24.1493 13.6232 24.1499 13.7549V20.1841C24.1499 20.3163 24.1149 20.4461 24.0485 20.5604C23.9821 20.6748 23.8867 20.7695 23.7719 20.8351L18.1469 24.0496C18.0336 24.1144 17.9054 24.1484 17.7749 24.1484C17.6444 24.1484 17.5162 24.1144 17.4029 24.0496L12.1499 21.0496L6.8969 24.0511C6.7836 24.1159 6.65538 24.1499 6.5249 24.1499C6.39442 24.1499 6.2662 24.1159 6.1529 24.0511L0.527902 20.8366C0.413112 20.771 0.317692 20.6763 0.251311 20.5619C0.184929 20.4476 0.149945 20.3178 0.149902 20.1856L0.149902 13.7564C0.149945 13.6242 0.184929 13.4944 0.251311 13.3801C0.317692 13.2657 0.413112 13.171 0.527902 13.1054L5.7749 10.1069V4.11424C5.77494 3.98204 5.80993 3.8522 5.87631 3.73787C5.94269 3.62355 6.03811 3.5288 6.1529 3.46323L11.7779 0.248662ZM6.5249 11.4059L2.4119 13.7564L6.5249 16.107L10.6379 13.7564L6.5249 11.4059ZM11.3999 15.0495L7.2749 17.406V22.1071L11.3999 19.7506V15.0495ZM12.8999 19.7506L17.0249 22.1071V17.406L12.8999 15.0495V19.7506ZM13.6619 13.7564L17.7749 16.107L21.8879 13.7564L17.7749 11.4059L13.6619 13.7564ZM17.0249 10.1069V5.40577L12.8999 7.76381V12.4649L17.0249 10.1069ZM11.3999 12.4649V7.76381L7.2749 5.40577V10.1069L11.3999 12.4649ZM8.0369 4.11424L12.1499 6.46479L16.2629 4.11424L12.1499 1.76369L8.0369 4.11424ZM22.6499 15.0495L18.5249 17.406V22.1071L22.6499 19.7506V15.0495ZM5.7749 22.1071V17.406L1.6499 15.0495V19.7506L5.7749 22.1071Z" fill="currentColor" stroke="currentColor" strokeWidth="0.3"/>
                    </svg>
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/process" className="inline-flex items-center font-medium text-[#131312] text-lg">
              View detailed process
              <ArrowRight className="ml-2 size-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
              radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
            `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-white text-sm font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-14 sm:w-auto sm:text-base"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
